import { useState, useRef, useEffect } from "react";
import "./Editemployeeoverlay.css";
import ImageCropper from "../../../../components/imageCropper/ImageCropper";
import { uiAlert } from "../../../../components/alerts/customAlert/uiAlert";
import { uploadEmployeeImageUrl } from "../../../../api/endpoints";
import { uploadloadOneImage } from "../../../../tools/uploadImage";
import { ImageUploadLoader } from "../../../../components/uxCollections/Loaders";
import { GiReturnArrow } from "react-icons/gi";
import { IoReturnDownBack, IoReturnDownForward } from "react-icons/io5";
import { BiMoney } from "react-icons/bi";

/**
 * EditEmployeeOverlay
 *
 * Props:
 *  employee  – employee object (see shape below)
 *  stores    – [{ _id, storeName }]
 *  onClose   – () => void
 *  onSave    – (updatedEmployee) => void
 *
 * Employee shape expected:
 *  { _id, name, role, employeeImage, salary, storeAssigned, storeAssignedID,
 *    phone, username, password, gender, address, employmentDate,
 *    permissions: { dashboard, customers, salesRecords, storeProfile, warehouse },
 *    actions: { editProduct, moveProducts } }
 */

const DEFAULT_PERMISSIONS = {
  dashboard: false,
  customers: false,
  salesRecords: false,
  storeProfile: false,
  warehouse: false,
  onlineOrders: false,
};

const DEFAULT_ACTIONS = {
  canEditProducts: false,
  canMoveProducts: false,
  canMakeSales: false,
  canProcessReturns: false,
};

const PERMISSION_META = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: (
      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <rect x='3' y='3' width='7' height='7' rx='1' />
        <rect x='14' y='3' width='7' height='7' rx='1' />
        <rect x='3' y='14' width='7' height='7' rx='1' />
        <rect x='14' y='14' width='7' height='7' rx='1' />
      </svg>
    ),
  },
  {
    key: "customers",
    label: "Customers",
    icon: (
      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2' />
        <circle cx='9' cy='7' r='4' />
        <path d='M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75' />
      </svg>
    ),
  },
  {
    key: "salesRecords",
    label: "Sales records",
    icon: (
      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <polyline points='22 12 18 12 15 21 9 3 6 12 2 12' />
      </svg>
    ),
  },
  {
    key: "storeProfile",
    label: "Store profile",
    icon: (
      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z' />
        <polyline points='9 22 9 12 15 12 15 22' />
      </svg>
    ),
  },
  {
    key: "warehouse",
    label: "Warehouse",
    icon: (
      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <rect x='2' y='7' width='20' height='14' rx='2' />
        <path d='M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16' />
      </svg>
    ),
  },
  {
    key: "onlineOrders",
    label: "online orders",
    icon: (
      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z' />
      </svg>
    ),
  },
];

const ACTION_META = [
  {
    key: "canEditProducts",
    label: "Edit product",
    icon: (
      <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7' />
        <path d='M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z' />
      </svg>
    ),
  },
  {
    key: "canMoveProducts",
    label: "Move products",
    icon: (
      <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M5 12h14M12 5l7 7-7 7' />
      </svg>
    ),
  },
  {
    key: "canMakeSales",
    label: "Process sales",
    icon: <BiMoney size={14} />,
  },
  {
    key: "canProcessReturns",
    label: "Process returns",
    icon: <IoReturnDownBack size={14} />,
  },
];

function getInitials(name = "") {
  return name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("");
}

export default function EditEmployeeOverlay({ employee, stores = [], onClose, onSave }) {
  const [form, setForm] = useState({
    name: employee?.employeeName ?? "",
    role: employee?.position ?? "",
    salary: employee?.salary ?? "",
    storeAssigned: employee?.storeAssigned ?? { storeID: "", storeName: "" },
    phone: employee?.employeePhoneNumber ?? "",
    password: employee?.password ?? "",
    permissions: { ...DEFAULT_PERMISSIONS, ...(employee?.permissions ?? {}) },
    actions: { ...DEFAULT_ACTIONS, ...(employee?.allowedActions ?? {}) },
  });

  console.log(form.actions, employee?.actions, form.permissions, employee?.permissions);

  const [imagePreview, setImagePreview] = useState(employee?.employeeImage ?? null);
  const [imageFile, setImageFile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [croppedImageBuffer, setCroppedImageBuffer] = useState(null); //
  const [openImageCropper, setOpenImageCropper] = useState(false);
  const [employeeImageUrl, setEmployeeImageUrl] = useState(null);
  const [employeeImageUrlStage, setEmployeeImageUrlStage] = useState(null); // for uploading image to cropper
  const [loading, setLoading] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (croppedImageBuffer) {
      sendImageToBackend();
    }
  }, [croppedImageBuffer]);

  const sendImageToBackend = async () => {
    setLoadingImage(true);
    try {
      let { imageUrl } = await uploadloadOneImage("", "", uploadEmployeeImageUrl, "EmployeeImage", false, croppedImageBuffer);
      setEmployeeImageUrl(imageUrl);
    } catch (error) {
      alert("Error uploading image. Please try again.");
    } finally {
      setLoadingImage(false);
    }
    /* setLoading(false); */
  };

  function handleField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handlePermission(key) {
    setForm((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
    }));
  }

  function handleAction(key) {
    setForm((prev) => ({
      ...prev,
      actions: { ...prev.actions, [key]: !prev.actions[key] },
    }));
  }

  function handleImageChange(e) {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      setImageFile(file);
      //  setImagePreview(URL.createObjectURL(file));
      const reader = new FileReader();
      reader.onload = function (evt) {
        setEmployeeImageUrlStage(evt.target.result);
        setOpenImageCropper(true);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.log(error);
      uiAlert("Error occurred while uploading your image, please try again.", "error");
    }
  }

  async function handleSave() {
    setSaving(true);
    // In your real app: upload imageFile first if it exists, then patch employee
    await onSave?.({ ...form, imageFile: employeeImageUrl, _id: employee?._id });
    setSaving(false);
    onClose();
  }

  const selectedStore = stores.find((s) => s._id === form.storeAssigned.storeID);

  return (
    <>
      <div className='eeo-backdrop' onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className='eeo-card'>
          {/* Header */}
          <div className='eeo-header'>
            <div>
              <h2 className='eeo-header-title'>Edit employee</h2>
              <p className='eeo-header-sub'>{employee?.name}</p>
            </div>
            <button className='eeo-close-btn' onClick={onClose}>
              ×
            </button>
          </div>

          {/* Body */}
          <div className='eeo-body'>
            {/* Avatar */}
            <div>
              <p className='eeo-section-title'>Profile photo</p>
              <div className='eeo-avatar-row'>
                <div className='eeo-avatar-wrap'>
                  {employeeImageUrl ? (
                    loadingImage ? (
                      <ImageUploadLoader />
                    ) : (
                      <img className='eeo-avatar-img' src={employeeImageUrl} alt={form.name} />
                    )
                  ) : (
                    <div className='eeo-avatar-initials'>{getInitials(form.name)}</div>
                  )}
                  <div className='eeo-avatar-upload-btn' onClick={() => fileInputRef.current?.click()} title='Change photo'>
                    <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2.5'>
                      <path d='M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z' />
                      <circle cx='12' cy='13' r='4' />
                    </svg>
                  </div>
                  <input ref={fileInputRef} type='file' accept='image/*' className='eeo-avatar-file-input' onChange={handleImageChange} />
                </div>
                <div className='eeo-avatar-info'>
                  <p className='eeo-avatar-info-name'>{form.name || "Employee"}</p>
                  <p className='eeo-avatar-info-hint'>Click the camera icon to upload a new photo. JPG or PNG, max 2MB.</p>
                </div>
              </div>
            </div>

            <div className='eeo-divider' />

            {/* Basic info */}
            <div>
              <p className='eeo-section-title'>Basic information</p>
              <div className='eeo-form-grid'>
                <div className='eeo-field'>
                  <label className='eeo-field-label'>Full name</label>
                  <input
                    className='eeo-field-input'
                    type='text'
                    value={form.name}
                    onChange={(e) => handleField("name", e.target.value)}
                    placeholder='Full name'
                  />
                </div>

                <div className='eeo-field'>
                  <label className='eeo-field-label'>Role</label>
                  <input
                    className='eeo-field-input'
                    type='text'
                    value={form.role}
                    onChange={(e) => handleField("role", e.target.value)}
                    placeholder='e.g. Sales girl, Madam'
                  />
                </div>

                <div className='eeo-field'>
                  <label className='eeo-field-label'>Salary (₦)</label>
                  <input
                    className='eeo-field-input'
                    type='number'
                    min={0}
                    value={form.salary}
                    onChange={(e) => handleField("salary", e.target.value)}
                    placeholder='e.g. 45000'
                  />
                </div>

                <div className='eeo-field'>
                  <label className='eeo-field-label'>Store assigned</label>
                  <select className='eeo-field-select' value={form.storeAssigned} onChange={(e) => handleField("storeAssignedID", e.target.value)}>
                    <option value=''>Select store</option>
                    {stores.map((s) => (
                      <option key={s._id} value={{ storeID: s._id, storeName: s.storeName }}>
                        {s.storeName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className='eeo-divider' />

            {/* Credentials */}
            <div>
              <p className='eeo-section-title'>Contact & credentials</p>
              <div className='eeo-form-grid'>
                <div className='eeo-field'>
                  <label className='eeo-field-label'>Phone number</label>
                  <input
                    className='eeo-field-input'
                    type='tel'
                    value={form.phone}
                    onChange={(e) => handleField("phone", e.target.value)}
                    placeholder='+234...'
                  />
                </div>

                <div className='eeo-field'>
                  <label className='eeo-field-label'>Password</label>
                  <div className='eeo-pw-wrap'>
                    <input
                      className='eeo-field-input'
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => handleField("password", e.target.value)}
                      placeholder='New password'
                      autoComplete='new-password'
                    />
                    <button className='eeo-pw-toggle' type='button' onClick={() => setShowPassword((v) => !v)} tabIndex={-1}>
                      {showPassword ? (
                        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                          <path d='M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94' />
                          <path d='M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19' />
                          <line x1='1' y1='1' x2='23' y2='23' />
                        </svg>
                      ) : (
                        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                          <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
                          <circle cx='12' cy='12' r='3' />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className='eeo-divider' />

            {/* Permissions */}
            <div>
              <p className='eeo-section-title'>Allowed access</p>
              <div className='eeo-perm-grid'>
                {PERMISSION_META.map(({ key, label, icon }) => {
                  const on = form.permissions[key];
                  return (
                    <div key={key} className={`eeo-perm-item${on ? " eeo-perm-item--on" : ""}`} onClick={() => handlePermission(key)}>
                      <div className='eeo-perm-item-left'>
                        <div className='eeo-perm-icon' style={{ color: on ? "#2563eb" : "#64748b" }}>
                          {icon}
                        </div>
                        <span className='eeo-perm-label'>{label}</span>
                      </div>
                      <div className={`eeo-toggle${on ? " eeo-toggle--on" : ""}`}>
                        <div className='eeo-toggle-thumb' />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className='eeo-divider' />

            {/* Actions */}
            <div>
              <p className='eeo-section-title'>Allowed actions</p>
              <div className='eeo-action-grid'>
                {ACTION_META.map(({ key, label, icon }) => {
                  const on = form.actions[key];
                  return (
                    <div key={key} className={`eeo-action-item${on ? " eeo-action-item--on" : ""}`} onClick={() => handleAction(key)}>
                      <div className='eeo-action-check'>
                        {on && (
                          <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='3'>
                            <polyline points='20 6 9 17 4 12' />
                          </svg>
                        )}
                      </div>
                      <div style={{ color: on ? "#2563eb" : "#64748b", display: "flex", marginRight: 4 }}>{icon}</div>
                      <span className='eeo-action-label'>{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className='eeo-footer'>
            <button className='eeo-cancel-btn' onClick={onClose}>
              Cancel
            </button>
            <button className='eeo-save-btn' onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>
      {openImageCropper && (
        <ImageCropper
          readImageUrl={employeeImageUrlStage}
          setCroppedImageUrl={setEmployeeImageUrlStage}
          setCroppedImageBolb={setCroppedImageBuffer}
          closeModal={setOpenImageCropper}
          borderRadius={"20%"}
        />
      )}
    </>
  );
}
